/**
 * PortTimelineDataProvider - Listens for port timeline data requests from popups
 *
 * This component runs in the main window and handles POPUP_INIT requests
 * for 'port-timeline' type by fetching port call history and responding.
 */
import { useEffect, useState, useCallback } from 'react';
import { usePortCallsHistory } from '@ais-ui/api';
import {
  usePopupMessages,
  MainWindowActions,
  MessageType,
  PopupMessage,
  PopupInitPayload,
} from './PopupEvents';

interface ActiveRequest {
  popupId: string;
  locode: string;
  dateRange?: { from?: Date; to?: Date };
}

export const PortTimelineDataProvider = (): null => {
  const [activeRequest, setActiveRequest] = useState<ActiveRequest | null>(
    null,
  );
  // Track if this is the first response for a request (vs a refresh)
  const [isInitialRequest, setIsInitialRequest] = useState(false);

  // Fetch data when we have an active request
  const { data, isLoading, isFetching, isPending, isError, refetch } =
    usePortCallsHistory(activeRequest?.locode, activeRequest?.dateRange);

  // In React Query v5, we need to check if the query is actually ready
  // isPending = no data yet, isLoading = fetching for first time, isFetching = any fetch in progress
  const isQueryLoading = isPending || isLoading || isFetching;

  // Determine if we have the data we need
  const hasRequiredData =
    activeRequest && activeRequest.locode ? data !== undefined : false;

  // When activeRequest changes, refetch if we have a locode
  useEffect(() => {
    if (activeRequest?.locode) {
      refetch();
    }
  }, [activeRequest?.locode, activeRequest?.dateRange, refetch]);

  // When data arrives, send it to the popup
  useEffect(() => {
    // Only respond when we have an active request and query has completed
    if (activeRequest && !isQueryLoading) {
      const { popupId } = activeRequest;

      if (isError) {
        // On initial request, send error so popup knows to stop loading
        // On refresh, silently fail to keep existing data
        if (isInitialRequest) {
          MainWindowActions.sendInitResponse(popupId, {
            records: [],
            error: 'Failed to fetch port call history',
          });
        }
        setActiveRequest(null);
        setIsInitialRequest(false);
        return;
      }

      if (hasRequiredData && data) {
        MainWindowActions.sendInitResponse(popupId, {
          records: data.records || [],
        });
        setActiveRequest(null);
        setIsInitialRequest(false);
      }
    }
  }, [
    data,
    isLoading,
    isFetching,
    isPending,
    isQueryLoading,
    isError,
    activeRequest,
    hasRequiredData,
    isInitialRequest,
  ]);

  // Listen for port-timeline requests
  usePopupMessages(
    useCallback((msg: PopupMessage) => {
      if (msg.type === MessageType.POPUP_INIT) {
        const payload = msg.payload as PopupInitPayload;

        if (payload.popupType === 'port-timeline') {
          const params = payload.params as {
            locode: string;
            dateFrom?: string;
            dateTo?: string;
          };

          const dateRange = {
            from: params.dateFrom ? new Date(params.dateFrom) : undefined,
            to: params.dateTo ? new Date(params.dateTo) : undefined,
          };

          // Set new active request - this will trigger the query
          setIsInitialRequest(true);
          setActiveRequest({
            popupId: payload.popupId,
            locode: params.locode,
            dateRange,
          });
        }
      }
    }, []),
    [],
  );

  return null;
};
